// All comments in English.
import { http } from "@/core/http";
import type { Pagination } from "@/types/common";

/** Shared types aligned with OpenAPI */
export type RiskLevel = "High" | "Medium" | "Low";

/** -1 = decrease, 0 = no-change/unknown, 1 = increase */
export type LicenseDeltaFlag = -1 | 0 | 1;

export interface RiskCompanyItem {
  company_name: string;
  active_seats: number;
  used_users: number;
  ur_cap: number;
  slope_4m: number | null;
  coverage_month: number;
  months_available: number;
  confidence: number;
  overuse_index: number;
  /** Align to spec: -1/0/1 instead of boolean */
  license_delta_flag: LicenseDeltaFlag;
  risk_label: string;
  risk_level: RiskLevel;
  risk_score_final: number;
  explain_codes?: string[];
}

export interface RiskProductItem {
  company_name: string;
  product: string;
  active_seats: number;
  used_users: number;
  ur_cap: number;
  slope_4m: number | null;
  overuse_index: number;
  /** Align to spec: -1/0/1 instead of boolean */
  license_delta_flag: LicenseDeltaFlag;
  risk_label: string;
  risk_level: RiskLevel;
  risk_score_final: number;
  explain_codes?: string[];
  confidence: number;
  /** backend default 0 */
  seats_mom_delta: number;
}

export interface RiskCompanyResponse {
  as_of_month: string;
  product?: string | null;
  pagination: Pagination;
  items: RiskCompanyItem[];
}

export interface RiskProductResponse {
  as_of_month: string;
  pagination: Pagination;
  items: RiskProductItem[];
}

/** GET /autodesk_usage/risk/products */
export async function getRiskProducts(params: {
  product?: string | null;
  risk_level?: string | null;
  behavior?: string | null;
  seat_band?: string | null;
  /** Align to spec: -1/0/1; null to omit the filter */
  license_delta_flag?: LicenseDeltaFlag | null;
  /** Keep as-is unless spec requires numeric as well */
  overuse_flag?: boolean | null;
  as_of_month?: string | null;
  page?: number;
  page_size?: number;
  /** default "-risk_score_final" */
  sort?: string;
}) {
  const { data } = await http.get<RiskProductResponse>(
    "/autodesk_usage/risk/products",
    { params }
  );
  return data;
}

/** ---------- Backward-compat wrappers (keep old names, call correct endpoints) ---------- */

/**
 * Old name: "top churn" list.
 * Now use /autodesk_usage/risk/products with product and sort by -risk_score_final.
 * This preserves the previous function signature used by pages/components.
 */
export async function getTopChurnRiskCustomers(params: {
  product: string;
  page_size?: number;
  page?: number;
  as_of_month?: string | null;
}) {
  const { product, page_size = 10, page = 1, as_of_month = null } = params;
  return getRiskProducts({
    product,
    page_size,
    page,
    as_of_month,
    // Highest risk first equals default "-risk_score_final"
    sort: "-risk_score_final",
  });
}

/**
 * Old name: "sales vs usage" series.
 * Map to /autodesk_usage/dashboard/aggregate-licenses which returns monthly
 * totals of active seats and used users.
 */
export interface SalesVsUsagePoint {
  month: string;
  total_active_seats: number;
  total_used_users: number;
}
export interface SalesVsUsageSeriesResponse {
  product: string;
  as_of_month: string;
  series: SalesVsUsagePoint[];
}

export async function getSalesVsUsageSeries(params: {
  product: string;
  months?: number; // default 12
  as_of_month?: string | null;
}) {
  const { product, months = 12, as_of_month = null } = params;
  const { data } = await http.get<SalesVsUsageSeriesResponse>(
    "/autodesk_usage/dashboard/aggregate-licenses",
    { params: { product, months, as_of_month } }
  );
  return data;
}

/**
 * Backward-compatible helper for "Top Holders".
 * It maps to GET /autodesk_usage/risk/products with sort by -active_seats.
 */
export async function getProductTopHolders(params: {
  product: string;
  page_size?: number;   // default 10 to match old UI
  page?: number;        // default 1
  as_of_month?: string | null;
}) {
  const {
    product,
    page_size = 10,
    page = 1,
    as_of_month = null,
  } = params;

  return getRiskProducts({
    product,
    page_size,
    page,
    as_of_month,
    // Highest active seats first (descending)
    sort: "-active_seats",
  });
}
